import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://bakelore.dxbhost.agency/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Unwrap data from response for consistency
    // Backend returns { success: true, data: {...} }
    if (response.data?.data !== undefined) {
      return { ...response, data: response.data.data };
    }
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on auth page
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
    }
    
    // Log error for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });
    
    return Promise.reject(error);
  }
);

// ==================== LOCATIONS API ====================
export const locationsAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/locations');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error; // Re-throw to trigger fallback in LocationContext
    }
  },
  
  getById: async (id) => {
    const response = await api.get(`/locations/${id}`);
    return response.data;
  },
};

// ==================== CATEGORIES API ====================
export const categoriesAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/categories');
      const categories = Array.isArray(response.data) ? response.data : [];
      // Transform image_url to image for frontend compatibility
      return categories.map(cat => ({
        ...cat,
        image: cat.image_url || cat.image || 'https://images.unsplash.com/photo-1486427944299-d1955d23e34d?w=400&h=400&fit=crop'
      }));
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },
  
  getById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },
  
  getSubcategories: async (categoryId) => {
    try {
      const response = await api.get(`/categories/${categoryId}/subcategories`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      return [];
    }
  },
};

// Helper function to transform product data from backend to frontend format
const transformProduct = (product) => {
  if (!product) return product;
  return {
    ...product,
    // Transform image_url to image for frontend compatibility
    image: product.image_url || product.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=400&fit=crop',
    // Keep original price or calculate from first variant
    price: product.price || (product.variants && product.variants[0]?.price) || 0,
  };
};

// ==================== PRODUCTS API ====================
export const productsAPI = {
  getAll: async (params = {}) => {
    try {
      // params: { locationId, categoryId, subcategoryId, search, is_trending, is_festival }
      const response = await api.get('/products', { params });
      // Transform and ensure we always return an array
      const products = Array.isArray(response.data) ? response.data : [];
      return products.map(transformProduct);
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },
  
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return transformProduct(response.data);
  },
  
  getTrending: async (locationId) => {
    try {
      const response = await api.get('/products', { 
        params: { locationId, is_trending: true } 
      });
      const products = Array.isArray(response.data) ? response.data : [];
      return products.map(transformProduct);
    } catch (error) {
      console.error('Error fetching trending products:', error);
      return [];
    }
  },
  
  getFestival: async (locationId) => {
    try {
      const response = await api.get('/products', { 
        params: { locationId, is_festival: true } 
      });
      const products = Array.isArray(response.data) ? response.data : [];
      return products.map(transformProduct);
    } catch (error) {
      console.error('Error fetching festival products:', error);
      return [];
    }
  },
  
  getVariants: async (productId, locationId) => {
    try {
      const response = await api.get(`/products/${productId}/variants`, {
        params: { locationId }
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching variants:', error);
      return [];
    }
  },
};

// ==================== OFFERS API ====================
export const offersAPI = {
  getAll: async (params = {}) => {
    // params: { location_id, active_only }
    const response = await api.get('/offers', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/offers/${id}`);
    return response.data;
  },
};

// ==================== COUPONS API ====================
export const couponsAPI = {
  getAll: async (params = {}) => {
    // params: { location_id, active_only }
    const response = await api.get('/coupons', { params });
    return response.data;
  },
  
  validate: async (code, data) => {
    // data: { cart_total, location_id, user_id }
    const response = await api.post(`/coupons/${code}/validate`, data);
    return response.data;
  },
  
  apply: async (code, orderId) => {
    const response = await api.post(`/coupons/apply`, { code, order_id: orderId });
    return response.data;
  },
};

// ==================== CART API ====================
export const cartAPI = {
  get: async () => {
    try {
      const response = await api.get('/cart');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Return empty cart structure on error
      return { items: [] };
    }
  },
  
  addItem: async (data) => {
    // data: { product_variant_id, quantity, custom_text? }
    const response = await api.post('/cart', data);
    return response.data;
  },
  
  updateItem: async (itemId, quantity) => {
    const response = await api.put(`/cart/${itemId}`, { quantity });
    return response.data;
  },
  
  removeItem: async (itemId) => {
    const response = await api.delete(`/cart/${itemId}`);
    return response.data;
  },
  
  clear: async () => {
    const response = await api.delete('/cart');
    return response.data;
  },
};

// ==================== ORDERS API ====================
export const ordersAPI = {
  create: async (orderData) => {
    // orderData: {
    //   location_id, delivery_type, delivery_date, delivery_time,
    //   items: [{ product_variant_id, quantity }],
    //   coupon_code?, delivery_address?, special_instructions?
    // }
    const response = await api.post('/orders', orderData);
    return response.data;
  },
  
  createGuest: async (orderData) => {
    // orderData includes guest_email, guest_name, guest_phone
    const response = await api.post('/orders/guest', orderData);
    return response.data;
  },
  
  getAll: async (params = {}) => {
    // params: { status, locationId }
    const response = await api.get('/orders', { params });
    // Backend returns { orders: [...], pagination: {...} }
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },
  
  getByIdPublic: async (id) => {
    // Public endpoint for order confirmation (no auth required)
    const response = await api.get(`/orders/${id}/public`);
    return response.data;
  },
  
  getUserOrders: async () => {
    const response = await api.get('/orders/user/me');
    return response.data;
  },
  
  cancelOrder: async (id) => {
    const response = await api.put(`/orders/${id}/cancel`);
    return response.data;
  },
};

// ==================== WISHLIST API ====================
export const wishlistAPI = {
  get: async () => {
    const response = await api.get('/wishlist');
    // Transform products in wishlist items
    // Backend returns product_variant which contains the product
    const items = Array.isArray(response.data) ? response.data : [];
    return items.map(item => ({
      ...item,
      product: item.product_variant?.product ? transformProduct(item.product_variant.product) : null,
      variants: item.product_variant ? [item.product_variant] : []
    }));
  },
  
  getAll: async () => {
    // Alias for get()
    return wishlistAPI.get();
  },
  
  addItem: async (variantId, locationId) => {
    const response = await api.post('/wishlist', { 
      product_variant_id: variantId,
      location_id: locationId 
    });
    const item = response.data;
    // Transform product in the returned item (from variant)
    return {
      ...item,
      product: item.product_variant?.product ? transformProduct(item.product_variant.product) : null
    };
  },
  
  removeItem: async (itemId) => {
    const response = await api.delete(`/wishlist/${itemId}`);
    return response.data;
  },
};

// ==================== NOTIFICATIONS API ====================
export const notificationsAPI = {
  get: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },
  
  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },
  
  delete: async (notificationId) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

// ==================== AUTH API ====================
export const authAPI = {
  register: async (data) => {
    // data: { email, password, role: 'customer', location_id? }
    const response = await api.post('/auth/register', data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
  
  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },
};

// ==================== STOCK API (for availability checks) ====================
export const stockAPI = {
  checkAvailability: async (variantId, locationId) => {
    const response = await api.get(`/stocks/${locationId}/variant/${variantId}`);
    return response.data;
  },
  
  getLocationStock: async (locationId) => {
    const response = await api.get(`/stocks/${locationId}`);
    return response.data;
  },
};

// ==================== SHIPPING FEE & GST API ====================
export const shippingAPI = {
  calculateShippingFee: async (data) => {
    const response = await api.post('/shipping-fees/calculate', data);
    return response.data;
  },
  
  getGSTSettings: async () => {
    const response = await api.get('/shipping-fees/gst-settings');
    return response.data;
  },
};

// ==================== LOCATION ROUTING API ====================
export const locationRoutingAPI = {
  checkServiceability: async (pincode) => {
    const response = await api.post('/location/check-serviceability', { pincode });
    return response.data;
  },
  
  findByPincode: async (pincode) => {
    const response = await api.post('/location/find-by-pincode', { pincode });
    return response.data;
  },
  
  getPincodeInfo: async (pincode) => {
    const response = await api.get(`/location/pincode-info/${pincode}`);
    return response.data;
  },
  
  getServiceMap: async () => {
    const response = await api.get('/location/service-map');
    return response.data;
  },
  
  calculateDistance: async (pincode, locationId) => {
    const response = await api.post('/location/calculate-distance', { 
      pincode, 
      location_id: locationId 
    });
    return response.data;
  },
};

// Export all APIs
const apiService = {
  locations: locationsAPI,
  categories: categoriesAPI,
  products: productsAPI,
  offers: offersAPI,
  coupons: couponsAPI,
  cart: cartAPI,
  orders: ordersAPI,
  wishlist: wishlistAPI,
  auth: authAPI,
  stock: stockAPI,
  shipping: shippingAPI,
  notifications: notificationsAPI,
  locationRouting: locationRoutingAPI,
};

export default apiService;
